import React from "react";
import { Box, Chip, Grid, Paper, Typography, useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { HPC_COLORS } from "../hpcTheme";

// Solid chip colours with white text read well on both the dark and the light theme.
const STATUS_CHIP_COLORS = {
  DRAFT: "#616161",
  SCHEDULED: "#1565c0",
  ACTIVE: "#2e7d32",
  EXPIRED: "#e65100",
  DISABLED: "#c62828",
  PENDING: "#8d6e00",
  REDEEMED: "#2e7d32",
  CANCELLED: "#616161",
  REVERSED: "#c62828",
};

const STATUS_LABELS = {
  DRAFT: "Draft",
  SCHEDULED: "Scheduled",
  ACTIVE: "Active",
  EXPIRED: "Expired",
  DISABLED: "Disabled",
  PENDING: "Pending",
  REDEEMED: "Redeemed",
  CANCELLED: "Cancelled",
  REVERSED: "Reversed",
};

/**
 * Colours for the promotion admin pages. The global dark theme's primary colour equals the
 * page background, so every control gets explicit colours: [100]-[300] tokens are light in
 * dark mode and dark in light mode.
 */
export const usePromotionStyles = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const accent = colors.greenAccent[300];

  const disabledSx = {
    "&.Mui-disabled": {
      color: colors.grey[400],
      borderColor: colors.grey[600],
      backgroundColor: theme.palette.action.disabledBackground,
      boxShadow: "none",
    },
  };

  const baseButton = { fontWeight: 700, textTransform: "none", whiteSpace: "nowrap" };

  const primaryButtonSx = {
    ...baseButton,
    backgroundColor: HPC_COLORS.hpuOrange,
    color: "#1a1200",
    border: `2px solid ${HPC_COLORS.hpuOrange}`,
    "&:hover": { backgroundColor: "#ffb84d", borderColor: "#ffb84d", color: "#1a1200" },
    ...disabledSx,
  };

  const outlined = (color) => ({
    ...baseButton,
    color,
    borderColor: color,
    borderWidth: 2,
    backgroundColor: "transparent",
    "&:hover": { borderColor: color, borderWidth: 2, backgroundColor: theme.palette.action.hover },
    ...disabledSx,
  });

  const secondaryButtonSx = outlined(colors.blueAccent[300]);
  const dangerButtonSx = outlined(colors.redAccent[300]);
  const successButtonSx = outlined(colors.greenAccent[300]);
  const neutralButtonSx = outlined(colors.grey[200]);

  const dangerContainedSx = {
    ...baseButton,
    backgroundColor: "#c62828",
    color: "#ffffff",
    "&:hover": { backgroundColor: "#b71c1c" },
    ...disabledSx,
  };

  const successContainedSx = {
    ...baseButton,
    backgroundColor: "#2e7d32",
    color: "#ffffff",
    "&:hover": { backgroundColor: "#1b5e20" },
    ...disabledSx,
  };

  // Inputs, selects, checkboxes and date pickers with visible borders and labels.
  const pageSx = {
    "& .MuiCheckbox-root": { color: colors.grey[300] },
    "& .MuiCheckbox-root.Mui-checked": { color: accent },
    "& .MuiCheckbox-root.Mui-disabled": { color: colors.grey[600] },
    "& .MuiOutlinedInput-root": { color: colors.grey[100] },
    "& .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.grey[500],
    },
    "& .MuiOutlinedInput-root:not(.Mui-error):hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.grey[300],
    },
    "& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
      borderColor: accent,
      borderWidth: 2,
    },
    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderStyle: "dashed",
    },
    "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: colors.grey[400] },
    "& .MuiInputLabel-root:not(.Mui-error)": { color: colors.grey[300] },
    "& .MuiInputLabel-root.Mui-focused:not(.Mui-error)": { color: accent },
    "& .MuiFormHelperText-root:not(.Mui-error)": { color: colors.grey[300] },
    "& .MuiSelect-icon": { color: colors.grey[300] },
    "& input[type='date']::-webkit-calendar-picker-indicator": {
      filter: isDark ? "invert(1)" : "none",
      cursor: "pointer",
    },
    "& .MuiFormControlLabel-label": { color: colors.grey[100] },
  };

  const gridSx = {
    "& .MuiDataGrid-root": { border: "none", color: colors.grey[100] },
    "& .MuiDataGrid-cell": {
      borderBottom: "none",
      display: "flex",
      alignItems: "center",
      fontSize: { xs: "0.78rem", sm: "0.88rem", md: "0.9rem", lg: "0.875rem" },
    },
    "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, color: colors.grey[100] },
    "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
      color: colors.grey[100],
    },
    "& .MuiTablePagination-root, & .MuiTablePagination-selectIcon, & .MuiDataGrid-footerContainer .MuiIconButton-root": {
      color: colors.grey[100],
    },
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
      outline: `2px solid ${accent}`,
      outlineOffset: -2,
    },
  };

  const tableSx = {
    "& .MuiTableCell-root": { color: colors.grey[100], borderColor: colors.grey[700] },
    "& .MuiTableHead-root .MuiTableCell-root": {
      backgroundColor: colors.blueAccent[700],
      fontWeight: 700,
      whiteSpace: "nowrap",
    },
  };

  return {
    theme,
    colors,
    isDark,
    accent,
    pageSx,
    gridSx,
    tableSx,
    primaryButtonSx,
    secondaryButtonSx,
    dangerButtonSx,
    successButtonSx,
    neutralButtonSx,
    dangerContainedSx,
    successContainedSx,
  };
};

/** Card used for each block of a page, matching the Manual SMS sections. */
export const Section = ({ title, subtitle, actions, children, colors }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 2, md: 2.5 },
      mb: 2,
      borderRadius: 2,
      backgroundColor: colors.primary[400],
      borderColor: colors.grey[700],
      minWidth: 0,
    }}
  >
    {(title || actions) && (
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5} flexWrap="wrap" mb={2}>
        <Box minWidth={0}>
          {title && (
            <Typography variant="h5" fontWeight={700} color={colors.grey[100]} lineHeight={1.3}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body2" color={colors.grey[300]} mt={0.25}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && <Box display="flex" gap={1} flexWrap="wrap">{actions}</Box>}
      </Box>
    )}
    {children}
  </Paper>
);

/** Small labelled figure, matching the Manual SMS stat tiles. */
export const StatTile = ({ label, value, hint, color, colors }) => (
  <Box
    sx={{
      p: 1.5,
      height: "100%",
      borderRadius: 2,
      border: `1px solid ${colors.grey[700]}`,
      borderLeft: `4px solid ${color || colors.grey[500]}`,
      backgroundColor: "action.hover",
      minWidth: 0,
    }}
  >
    <Typography variant="h3" fontWeight={800} color={colors.grey[100]} lineHeight={1.2} sx={{ wordBreak: "break-word" }}>
      {value}
    </Typography>
    <Typography variant="body2" fontWeight={600} color={colors.grey[200]}>
      {label}
    </Typography>
    {hint && (
      <Typography variant="caption" color={colors.grey[300]} display="block">
        {hint}
      </Typography>
    )}
  </Box>
);

/** Label / value pair inside a section grid. */
export const InfoField = ({ label, value, colors, xs = 12, sm = 6, md = 3 }) => (
  <Grid item xs={xs} sm={sm} md={md}>
    <Typography variant="caption" color={colors.grey[300]} display="block">
      {label}
    </Typography>
    <Box sx={{ color: colors.grey[100], fontSize: "1rem", wordBreak: "break-word" }}>
      {value === null || value === undefined || value === "" ? "—" : value}
    </Box>
  </Grid>
);

export const StatusChip = ({ status, size = "small" }) => (
  <Chip
    size={size}
    label={STATUS_LABELS[status] || status}
    sx={{
      backgroundColor: STATUS_CHIP_COLORS[status] || "#616161",
      color: "#ffffff",
      fontWeight: 700,
    }}
  />
);
